import { CatalogFlow, CatalogNode, nextCatalogId, normalizeCatalogNode } from './catalogDoc';
import {
  addDashboard,
  ensureBus,
  ensureController,
  ensureDevice,
  ensureLabel,
  ensurePoint,
  ensureTimer,
  link,
  startChain,
  wireToMqtt,
} from './catalogChain';
import {
  ControllerSnapshot,
  PointKind,
  SnapshotDevice,
  SnapshotPoint,
} from './peripherySnapshot';
import { wireStacklightLegacyGraph, STACKLIGHT_LEGACY_INPUT_LINES } from './catalogStacklightLegacy';
import { linkStacklightCommand } from './catalogStacklight';

export type FlowWizardLabelDest = {
  key: string;
  layoutId: string;
  widgetId: string;
};

export type FlowWizardBusDest = {
  of: string;
  busType: string;
  role?: string;
  label: string;
};

export type FlowWizardAlertDest = {
  key: string;
  enable: boolean;
  min?: number | null;
  max?: number | null;
  hysteresis?: number;
  alarmDelayMin?: number;
  resetDelayMin?: number;
  criticality?: number;
  title?: string;
  /** Which channels to fire (secrets stay in Alerting UI). */
  channels: Array<'pushover' | 'mail'>;
};

export type FlowWizardArgs = {
  flowName: string;
  hostname: string;
  controllerId: string;
  device: SnapshotDevice;
  keys: string[];
  dashboard: boolean;
  labels: FlowWizardLabelDest[];
  buses: FlowWizardBusDest[];
  alerts?: FlowWizardAlertDest[];
  snapshot: ControllerSnapshot;
  interval?: number;
  /** Sub-second GPIO poll (ms). Preferred over interval for panel inputs. */
  intervalMs?: number;
  /** stacklight-legacy channel wiring (GPIO → stacklight + optional modern Säule). */
  wireLocalActuators?: boolean;
  wireZigbeeUplink?: boolean;
  modernHostname?: string;
  modernControllerId?: string;
  modernOf?: string;
};

const COL = 160;
const ROW = 140;

export function pollOf(kind: PointKind): boolean | undefined {
  if (kind === 'poll') {
    return true;
  }
  if (kind === 'event') {
    return false;
  }
  return undefined;
}

export function pointValuePort(kind: PointKind): 'value' | 'event' {
  return kind === 'event' ? 'event' : 'value';
}

export function fieldBusesOf(snap: ControllerSnapshot): FlowWizardBusDest[] {
  return snap.connections
    .filter((conn) => conn.type && conn.type !== 'mqtt')
    .map((conn) => ({
      of: conn.id,
      busType: conn.type,
      role: conn.type === 'modbus' ? 'master' : undefined,
      label: conn.label || conn.id,
    }));
}

export function dashboardWidgetType(deviceType: string, keys: string[]): string {
  if (deviceType === 'stacklight') {
    return 'stacklight';
  }
  if (deviceType === 'sml') {
    return 'sml_meter';
  }
  if (deviceType === 'gps') {
    return 'gps_map';
  }
  if (deviceType === 'pn532') {
    return 'nfc_tag';
  }
  if (deviceType === 'vl53l0x') {
    return 'distance';
  }
  if (deviceType === 'sht21' || deviceType === 'modbus_slave') {
    return 'temperature';
  }
  if (deviceType === 'gpio_relay') {
    return 'relay';
  }
  if (keys.indexOf('temp') >= 0 || keys.indexOf('rh') >= 0) {
    return 'temperature';
  }
  return 'temperature';
}

export function dashboardPorts(widgetType: string, keys: string[]): Record<string, string> {
  if (widgetType === 'temperature') {
    const ports: Record<string, string> = {};
    const temp = keys.filter((key) => key === 'temp' || key === 'temperature')[0];
    const rh = keys.filter((key) => key === 'rh' || key === 'humidity')[0];
    if (temp) {
      ports[temp] = 'in';
    }
    if (rh) {
      ports[rh] = 'inRh';
    }
    if (!temp && keys[0]) {
      ports[keys[0]] = 'in';
    }
    return ports;
  }
  if (widgetType === 'sml_meter') {
    const ports: Record<string, string> = {};
    if (keys.indexOf('energy') >= 0) {
      ports.energy = 'in';
    }
    if (keys.indexOf('power') >= 0) {
      ports.power = 'power';
    }
    if (keys.indexOf('energy_reverse') >= 0) {
      ports.energy_reverse = 'reverse';
    }
    return ports;
  }
  if (widgetType === 'relay') {
    const ports: Record<string, string> = {};
    keys.forEach((key, index) => {
      ports[key] = `ch${index + 1}`;
    });
    return ports;
  }
  if (!keys[0]) {
    return {};
  }
  return { [keys[0]]: 'in' };
}

function mqttTopic(device: SnapshotDevice, key: string): string {
  if (device.type === 'stacklight' && key === 'status') {
    return '{base}';
  }
  return `{base}/${device.id}/${key}`;
}

function sourceBusOf(device: SnapshotDevice, snap: ControllerSnapshot): FlowWizardBusDest | null {
  const connection = device.connection;
  if (!connection) {
    return null;
  }
  return fieldBusesOf(snap).filter((bus) => bus.of === connection)[0] || null;
}

function pointKind(device: SnapshotDevice, key: string): PointKind {
  const found = device.points.filter((point) => point.key === key)[0];
  return found ? found.kind : 'poll';
}

function writeHost(
  snap: ControllerSnapshot,
  bus: FlowWizardBusDest,
  key: string,
): { of: string; key: string; deviceType?: string } {
  const onBus = snap.devices.filter((item) => item.connection === bus.of);
  const withKey = onBus.filter((item) => item.points.some((point) => point.key === key))[0];
  if (withKey) {
    return { of: withKey.id, key, deviceType: withKey.type };
  }
  const conn = snap.connections.filter((item) => item.id === bus.of)[0];
  if (conn && conn.points.some((point) => point.key === key)) {
    return { of: conn.id, key };
  }
  return { of: bus.of, key };
}

function col(stateX0: number, n: number): number {
  return stateX0 + n * COL;
}

function row(stateY0: number, n: number): number {
  return stateY0 + n * ROW;
}

/** Insert controller → existing periphery → selected points → destinations onto the catalog canvas. */
export function mergeFlowWizard(doc: CatalogFlow, args: FlowWizardArgs): CatalogFlow {
  const keys = args.keys.filter((key, index) => key && args.keys.indexOf(key) === index);
  if (!keys.length) {
    return doc;
  }

  if (
    args.device.type === 'gpio_input'
    && (args.wireLocalActuators || args.wireZigbeeUplink || args.modernHostname)
  ) {
    const state = startChain(doc);
    const graphLines = keys.map((key) => {
      const known = STACKLIGHT_LEGACY_INPUT_LINES.find((line) => line.key === key);
      return known
        ? {
          key: known.key,
          pin: known.pin,
          mode: known.mode,
          edge: known.edge,
          inverted: known.inverted,
        }
        : {
          key,
          pin: 0,
          mode: 'input',
          edge: 'change',
          inverted: true,
        };
    });
    wireStacklightLegacyGraph(state, {
      flowName: args.flowName,
      hostname: args.hostname,
      controllerId: args.controllerId,
      stacklightOf: args.modernOf || 'stacklight',
      lines: graphLines,
      keys,
      interval: args.interval,
      intervalMs: args.intervalMs,
      wirePanelInputs: true,
      wireLocalActuators: args.wireLocalActuators !== false,
      wireZigbeeUplink: args.wireZigbeeUplink === true,
      modernHostname: args.modernHostname,
      modernControllerId: args.modernControllerId,
      modernOf: args.modernOf,
      widget: args.dashboard ? 'stacklight' : 'none',
    });
    const layouts: string[] = [];
    for (const item of doc.layouts || []) {
      if (item && layouts.indexOf(item) < 0) {
        layouts.push(item);
      }
    }
    return {
      ...doc,
      id: doc.id || args.flowName,
      enable: doc.enable !== false,
      layouts,
      nodes: state.nodes.map(normalizeCatalogNode),
      edges: state.edges,
    };
  }

  const state = startChain(doc);
  const controller = ensureController(state, {
    flowName: args.flowName,
    hostname: args.hostname,
    controllerId: args.controllerId,
    device: { id: args.device.id, type: args.device.type, enable: true },
    widget: 'none',
  });
  const sourceBus = sourceBusOf(args.device, args.snapshot);
  const busX = sourceBus ? 1 : 0;
  const deviceX = sourceBus ? 2 : 1;
  const pointX = sourceBus ? 3 : 2;
  let destX = sourceBus ? 4 : 3;

  if (sourceBus) {
    const bus = ensureBus(state, {
      of: sourceBus.of,
      busType: sourceBus.busType,
      role: sourceBus.role,
      x: col(state.x0, busX),
      y: state.y0,
    });
    link(state, `${controller.id}.host`, `${bus.id}.host`);
    const device = ensureDevice(state, {
      of: args.device.id,
      deviceType: args.device.type,
      x: col(state.x0, deviceX),
      y: state.y0,
    });
    link(state, `${bus.id}.read`, `${device.id}.read`);
  } else if (
    args.device.type === 'stacklight'
    || args.device.type === 'gpio_relay'
    || args.device.type === 'gpio_led'
    || args.device.type === 'gpio_buzzer'
  ) {
    const device = ensureDevice(state, {
      of: args.device.id,
      deviceType: args.device.type,
      x: col(state.x0, deviceX),
      y: state.y0,
    });
    link(state, `${controller.id}.host`, `${device.id}.host`);
  } else {
    ensureDevice(state, {
      of: args.device.id,
      deviceType: args.device.type,
      x: col(state.x0, deviceX),
      y: state.y0,
    });
  }

  const deviceNode = state.nodes.filter((node) => node.type === 'device' && node.of === args.device.id)[0];
  const pollKeys = keys.filter((key) => pointKind(args.device, key) === 'poll');
  const timer = pollKeys.length
    ? ensureTimer(state, Number(args.interval) > 0 ? Math.round(Number(args.interval)) : 60, state.x0, state.y0 + 220)
    : undefined;

  const points: Record<string, { node: CatalogNode; kind: PointKind }> = {};
  keys.forEach((key, index) => {
    const kind = pointKind(args.device, key);
    const node = ensurePoint(state, {
      of: args.device.id,
      key,
      poll: pollOf(kind),
      x: col(state.x0, pointX),
      y: row(state.y0, index),
      deviceId: deviceNode?.id,
    });
    if (timer && kind === 'poll') {
      link(state, `${timer.id}.trigger`, `${node.id}.trigger`);
    }
    points[key] = { node, kind };
  });

  const mqttByKey: Record<string, CatalogNode> = {};
  const alertKeys = (args.alerts || [])
    .filter((item) => item.enable && item.key && (item.min != null || item.max != null))
    .map((item) => item.key);
  const mqttKeys = Array.from(new Set([
    ...(args.dashboard ? keys : []),
    ...alertKeys,
  ]));
  const stacklightZigbee = args.device.type === 'stacklight' && args.wireZigbeeUplink === true;
  const skipMqtt = stacklightZigbee
    || (args.device.type === 'gpio_relay' && args.wireZigbeeUplink === true);
  if (mqttKeys.length && !skipMqtt) {
    mqttKeys.forEach((key, index) => {
      const item = points[key];
      if (!item) {
        return;
      }
      mqttByKey[key] = wireToMqtt(state, {
        point: item.node,
        topic: mqttTopic(args.device, key),
        y: row(state.y0, index),
        xCalc: col(state.x0, destX),
        xMqtt: col(state.x0, destX),
        timer,
        from: pointValuePort(item.kind),
        both: args.device.type === 'gpio_relay' && args.dashboard === true,
      });
    });
    destX += 1;
  }

  if (args.dashboard) {
    const widgetType = dashboardWidgetType(args.device.type, keys);
    const relayZigbee = widgetType === 'relay' && args.wireZigbeeUplink === true;
    const dash = addDashboard(state, {
      widgetType,
      title: args.hostname ? `${args.hostname} · ${args.device.id}` : args.device.id,
      signalName: widgetType === 'stacklight' ? args.device.id : undefined,
      channelNames: widgetType === 'relay' ? keys.map((_, index) => `Relais ${index + 1}`) : undefined,
      x: col(state.x0, destX),
      y: state.y0,
    });
    destX += 1;
    const ports = dashboardPorts(widgetType, keys);
    Object.keys(ports).forEach((key) => {
      if (relayZigbee && points[key]) {
        link(state, `${points[key].node.id}.value`, `${dash.id}.${ports[key]}`, true);
        return;
      }
      if (mqttByKey[key]) {
        link(state, `${mqttByKey[key].id}.subscribe`, `${dash.id}.${ports[key]}`, widgetType === 'relay');
      }
    });
    if (widgetType === 'stacklight' && points.status) {
      linkStacklightCommand(state, {
        pointNodeId: points.status.node.id,
        controllerNodeId: controller.id,
        dashNodeId: dash.id,
        mqttNodeId: mqttByKey.status?.id,
        zigbee: stacklightZigbee,
        x: col(state.x0, destX),
        y: state.y0 + 140,
      });
    }
    if (widgetType === 'relay' && relayZigbee) {
      keys.forEach((key) => {
        if (!points[key]) {
          return;
        }
        linkStacklightCommand(state, {
          pointNodeId: points[key].node.id,
          controllerNodeId: controller.id,
          zigbee: true,
          both: true,
          x: col(state.x0, destX),
          y: state.y0 + 140,
        });
      });
    }
  }

  (args.alerts || []).forEach((alert, index) => {
    if (!alert.enable || !alert.key || (alert.min == null && alert.max == null)) {
      return;
    }
    const mqtt = mqttByKey[alert.key];
    if (!mqtt) {
      return;
    }
    const y = row(state.y0, index);
    const thrId = nextCatalogId('thr', state.nodes);
    const thr: CatalogNode = {
      id: thrId,
      type: 'threshold',
      x: col(state.x0, destX),
      y,
      title: alert.title || `${args.device.id}.${alert.key}`,
      hysteresis: alert.hysteresis,
      alarmDelayMin: alert.alarmDelayMin,
      resetDelayMin: alert.resetDelayMin,
    };
    if (alert.min != null) {
      thr.min = Number(alert.min);
    }
    if (alert.max != null) {
      thr.max = Number(alert.max);
    }
    state.nodes.push(thr);
    const ntfId = nextCatalogId('ntf', state.nodes);
    const channels = (alert.channels || []).filter((c) => c === 'pushover' || c === 'mail');
    state.nodes.push({
      id: ntfId,
      type: 'notify',
      x: col(state.x0, destX + 1),
      y,
      title: alert.title || `${args.hostname || args.device.id} · ${alert.key}`,
      criticality: alert.criticality != null ? alert.criticality : 2,
      channels: channels.length ? channels : ['pushover', 'mail'],
    });
    link(state, `${mqtt.id}.subscribe`, `${thrId}.in`);
    link(state, `${thrId}.event`, `${ntfId}.event`);
  });
  if ((args.alerts || []).some((a) => a.enable)) {
    destX += 2;
  }

  args.labels.forEach((dest, index) => {
    const item = points[dest.key];
    if (!item || !dest.layoutId || !dest.widgetId) {
      return;
    }
    const label = ensureLabel(state, {
      layout: dest.layoutId,
      widget: dest.widgetId,
      x: col(state.x0, destX),
      y: row(state.y0, index),
    });
    link(state, `${item.node.id}.${pointValuePort(item.kind)}`, `${label.id}.value`);
  });
  if (args.labels.some((dest) => dest.layoutId && dest.widgetId && points[dest.key])) {
    destX += 1;
  }

  args.buses.forEach((busDest, busIndex) => {
    const bus = ensureBus(state, {
      of: busDest.of,
      busType: busDest.busType,
      role: busDest.role,
      x: col(state.x0, destX),
      y: row(state.y0, busIndex),
    });
    link(state, `${controller.id}.host`, `${bus.id}.host`);
    keys.forEach((key, keyIndex) => {
      const item = points[key];
      if (!item) {
        return;
      }
      const target = writeHost(args.snapshot, busDest, key);
      if (target.of === args.device.id && target.key === key) {
        return;
      }
      const writeDevice = target.deviceType
        ? ensureDevice(state, {
          of: target.of,
          deviceType: target.deviceType,
          x: col(state.x0, destX + 1),
          y: row(state.y0, busIndex + keyIndex),
        })
        : undefined;
      if (writeDevice) {
        link(state, `${writeDevice.id}.write`, `${bus.id}.write`);
      }
      const destPoint = ensurePoint(state, {
        of: target.of,
        key: target.key,
        x: col(state.x0, destX + (writeDevice ? 2 : 1)),
        y: row(state.y0, busIndex + keyIndex),
        deviceId: writeDevice?.id,
      });
      link(state, `${item.node.id}.${pointValuePort(item.kind)}`, `${destPoint.id}.command`);
    });
  });

  const layouts: string[] = [];
  for (const item of doc.layouts || []) {
    if (item && layouts.indexOf(item) < 0) {
      layouts.push(item);
    }
  }
  for (const node of state.nodes) {
    if (node.type === 'label' && node.layout && layouts.indexOf(node.layout) < 0) {
      layouts.push(node.layout);
    }
  }
  return {
    ...doc,
    id: doc.id || args.flowName,
    enable: doc.enable !== false,
    layouts,
    nodes: state.nodes.map(normalizeCatalogNode),
    edges: state.edges,
  };
}

export function snapshotDevicePoints(device: SnapshotDevice): SnapshotPoint[] {
  return device.points.filter((point) => Boolean(point.key));
}
